// @flow

import React, { Fragment, memo, useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import { DialogTitle, InputAdornment } from '@mui/material';
import { voidFunc } from 'js/constants/PropTypeUtils';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import moment from 'moment-timezone';
import { grey } from '@mui/material/colors';

import Styles from './DateRangeInput.module.less';
import { useLangFile } from '../../context/LanguageContext';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../hooks/useFirebaseAnalytics';
import { formatDateAsYearMonthDate } from '../../helpers/DateUtils';
import { DatePicker } from '@mui/x-date-pickers';
import { DateRange } from '@mui/icons-material';

const DateRangeInput = (props: DateRangeInput.propTypes) => {
  const analytics = useFirebaseAnalytics();
  const LangFile = useLangFile();
  const [showDialog, setShowDialog] = useState(false);
  const [state, setState] = useState({ startDate: props.startDate, endDate: props.endDate });

  useEffect(() => {
    setState({ startDate: props.startDate, endDate: props.endDate });
  }, [props.startDate, props.endDate]);

  const setStartDate = useCallback((value) => {
    setState((current) => {
      if (current.endDate && moment(value).isAfter(current.endDate)) {
        return { startDate: value, endDate: value };
      }

      return { ...current, startDate: value };
    });
  }, []);

  const setEndDate = useCallback((value) => {
    setState((current) => {
      if (current.startDate && moment(value).isBefore(current.startDate)) {
        return { startDate: value, endDate: value };
      }

      return { ...current, endDate: value };
    });
  }, []);

  const handleDialogOpen = useCallback(() => {
    if (!props.disabled) {
      setShowDialog(true);
      props.onFocus();
    }
  }, [props.disabled, props.onFocus]);

  const handleDialogClose = useCallback(() => {
    setShowDialog(false);
    props.onBlur();
  }, [props.onBlur]);

  const commitChange = useCallback(() => {
    if (!state.startDate || !state.endDate) {
      return;
    }

    const since = moment(state.startDate).startOf('day');
    const until = moment(state.endDate).endOf('day');

    props.onChanged(since, until);
    handleDialogClose();

    analytics.logEvent(FIREBASE_EVENTS.WEATHER_HISTORY_DATE_RANGE, {
      since: formatDateAsYearMonthDate(state.startDate),
      until: formatDateAsYearMonthDate(state.endDate),
    });
  }, [props.onChanged, state, handleDialogClose]);

  const hasDateRange = state.startDate && state.endDate;
  const startYear = hasDateRange ? moment(state.startDate).format('YYYY') : '';
  const endYear = hasDateRange ? moment(state.endDate).format('YYYY') : '';
  const sameYear = startYear === endYear;

  const startText = state.startDate ? moment(state.startDate).format('DD. MMM') : '';
  const endText = state.endDate ? moment(state.endDate).format('DD. MMM') : '';
  const value = hasDateRange ? `${startText} - ${endText}` : '';

  const dateRangeYearText = props.disabled
    ? ''
    : hasDateRange
      ? sameYear
        ? `${moment(state.startDate).format('YYYY')}`
        : `${moment(state.startDate).format('YYYY')} - ${moment(state.endDate).format('YYYY')}`
      : '';

  return (
    <Fragment>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <TextField
          variant={props.variant}
          className={props.disabled ? Styles.disabled : Styles.Root}
          InputProps={{
            endAdornment: (
              <InputAdornment position={'end'} className={Styles.InputAdornment}>
                <DateRange />
              </InputAdornment>
            ),
            classes: { input: Styles.Input },
            readOnly: true,
          }}
          onClick={handleDialogOpen}
          value={props.disabled ? LangFile.DateRangeInput.disabled : value}
        />
        <Box
          className={Styles.Year}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            color: grey['500'],
            mx: 1.5,
            my: 0.5,
            fontSize: 12,
          }}
        >
          {dateRangeYearText}
        </Box>
      </Box>

      <Dialog open={showDialog} maxWidth={'sm'} onClose={handleDialogClose}>
        <DialogTitle>{LangFile.DateRangeInput.title}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
              alignItems: 'center',
              gap: 2,
              pt: 1,
            }}
          >
            <DatePicker
              label={LangFile.DateRangeInput.from}
              format={'DD/MM/YYYY'}
              value={state.startDate ? moment(state.startDate) : null}
              minDate={props.minDate ? moment(props.minDate) : undefined}
              maxDate={
                state.endDate || props.maxDate ? moment(state.endDate || props.maxDate) : undefined
              }
              disableFuture={true}
              onChange={setStartDate}
              slotProps={{ textField: { variant: props.variant, size: 'small' } }}
            />

            <DatePicker
              label={LangFile.DateRangeInput.to}
              format={'DD/MM/YYYY'}
              value={state.endDate ? moment(state.endDate) : null}
              minDate={
                state.startDate || props.minDate
                  ? moment(state.startDate || props.minDate)
                  : undefined
              }
              maxDate={props.maxDate ? moment(props.maxDate) : undefined}
              disableFuture={true}
              onChange={setEndDate}
              slotProps={{ textField: { variant: props.variant, size: 'small' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              px: 2,
            }}
          >
            <Button onClick={handleDialogClose}>{LangFile.DateRangeInput.cancel}</Button>
            <Button
              onClick={commitChange}
              color={'primary'}
              variant={'contained'}
              disabled={!state.startDate || !state.endDate}
            >
              {LangFile.DateRangeInput.select}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

DateRangeInput.propTypes = {
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  onChanged: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  showPeriod: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['outlined', 'standard', 'filled']),
};

DateRangeInput.defaultProps = {
  onChanged: voidFunc,
  onBlur: voidFunc,
  onFocus: voidFunc,
  variant: 'outlined',
  showPeriod: false,
  disabled: false,
};

export default memo(DateRangeInput);
